/**
 * 学习：内容库 / 已布置、文件夹、内容详情（预览）、上传、布置。参考 iOS Apps/Parent/Sources/Learn/。
 * 子页面用查询参数：?tab=assigned、?folder=<id>、?content=<id>（浏览器后退可用）。
 * 调试：?dev_sheet=upload（上传）/ assign（布置第一个 PDF）/ assigned（已布置）/ preview（配合 ?content= 打开预览）。
 */
import { Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { type Content, dataStore, select, takeDevSheet, useCurrentChild, useDevSheet, useStore } from "../../core";
import { Page, PageTitle, PillSegment } from "../../ui";
import { isPending } from "./activity";
import { AssignedList } from "./AssignedList";
import { AssignSheet } from "./AssignSheet";
import { ContentDetail } from "./ContentDetail";
import "./learn.css";
import { FolderView, LibraryView } from "./Library";
import { UploadSheet } from "./UploadFlow";

export function LearnPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const contentId = params.get("content");
  const folderId = params.get("folder");
  const tab = params.get("tab") === "assigned" ? "assigned" : "library";
  const [uploadOpen, setUploadOpen] = useDevSheet("upload");
  const [assigning, setAssigning] = useState<Content | null>(null);
  const child = useCurrentChild();
  const pendingCount = useStore((s) => (child ? select.assignmentsOf(s, child.id).filter(isPending).length : 0));

  useEffect(() => {
    if (takeDevSheet("assigned")) setParams({ tab: "assigned" }, { replace: true });
  }, [setParams]);

  // ?dev_sheet=assign：等数据同步下来后打开第一个 PDF 的布置页
  const contentCount = useStore((s) => Object.keys(s.contents).length);
  useEffect(() => {
    if (contentCount === 0 || !takeDevSheet("assign")) return;
    const list = Object.values(dataStore.getState().contents);
    setAssigning(list.find((c) => c.activityType === "pdf_reader") ?? list[0] ?? null);
  }, [contentCount]);

  const open = (patch: Record<string, string>) => navigate({ search: `?${new URLSearchParams(patch).toString()}` });
  // 从页面内进来的子页面用浏览器后退；直接打开的链接回到内容库
  const back = () => (location.key !== "default" ? navigate(-1) : setParams({}, { replace: true }));

  let view;
  if (contentId) {
    view = <ContentDetail contentId={contentId} onBack={back} onAssign={setAssigning} />;
  } else if (folderId) {
    view = <FolderView folderId={folderId} onBack={back} onOpenContent={(id) => open({ content: id })} onAssign={setAssigning} />;
  } else {
    view = (
      <Page>
        <PageTitle
          trailing={
            <button type="button" className="learn-upload-btn" onClick={() => setUploadOpen(true)}>
              <Upload size={16} strokeWidth={2.6} />
              <span>上传</span>
            </button>
          }
        >
          学习
        </PageTitle>
        <PillSegment
          items={[["library", "内容库"], ["assigned", `已布置 ${pendingCount}`]] as const}
          value={tab}
          onChange={(v) => setParams(v === "assigned" ? { tab: "assigned" } : {}, { replace: true })}
          height={38}
          stretch
        />
        {tab === "library" ? <LibraryView onOpenFolder={(id) => open({ folder: id })} onOpenContent={(id) => open({ content: id })} onAssign={setAssigning} /> : <AssignedList />}
      </Page>
    );
  }

  return (
    <>
      {view}
      <UploadSheet open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <AssignSheet content={assigning} onClose={() => setAssigning(null)} />
    </>
  );
}
